import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import StringUtil from '@leverege/string-util'
import ArrayUtil from '@leverege/array-util'

import { Button, ButtonGroup } from 'react-bootstrap'
import { getTagsForAttributes, TreeOrganizer, Util } from '@leverege/api-attributes'
import Slider from 'rc-slider'
import { Popup } from '@leverege/ui-elements'
import { Tags, LabelGroup, TextInput, Loading } from '~/views'
import { ScenarioActions, BreadcrumbActions } from '~/actions'

import BehaviorEditor from './BehaviorEditor'
import TimeCalc from './TimeCalc'
import TimelineView from './TimelineView'
import AttributeView from './AttributeView'
import TimelineGroup from './TimelineGroup'
import AttributeGroup from './AttributeGroup'
import TimeLabels from './TimeLabels'
import SavingIndicator from './SavingIndicator'
import CursorIndicator from './CursorIndicator'
import CursorTimeIndicator from './CursorTimeIndicator'

import style from './style.css'

class ScenarioScreen extends React.Component {
  
  static KF_MODE_KEYFRAME = 0

  static KF_MODE_LEFT = 1

  static KF_MODE_TIMELINE = 2

  static KF_MODE_RIGHT = 3

  static contextTypes = { 
    api : PropTypes.object.isRequired,
  };
  
  constructor( props ) {
    super( props );
    this.state = { tags : [],
                   scale : 1000, 
                   maxTime : ( 1000 * 60 * 60 ), 
                   range : { min : 0, max : 60 * 1000 },
                   cursorMode : TimeCalc.MODE_1000,
                   kfMode : ScenarioScreen.KF_MODE_KEYFRAME }
    this.treeOrganizer = new TreeOrganizer()
    this.timeCalc = new TimeCalc()
    this.timeCalc.scale = this.state.scale
    this.timeCalc.maxTime = this.state.maxTime
  }

  componentDidMount() {
    const { params } = this.props.match
    if ( params && params.projectId && params.scenarioId ) {
      this.props.dispatch( ScenarioActions.getScenario( params.projectId, params.scenarioId ) );
    }
  }

  componentWillReceiveProps( next ) {
    const { api } = this.context

    const bp = next.scenarioData.blueprint
    const sc = next.scenarioData.scenario
    if ( sc && bp ) {
      this.props.dispatch( BreadcrumbActions.setBreadcrumbs( 
        [ { name : 'Blueprints', link : `/project/${bp.projectId}/blueprint` }, 
          { name : bp.name, link : `/project/${bp.projectId}/blueprint/${bp.id}` },
          { name : 'Scenarios', link : `/project/${bp.projectId}/scenario` },
          { name : sc.name } ] ) )
    }
  }

  onMouseMove = ( evt ) => {
    let x = this.timeCalc.xFromEvent( evt )
    let t = this.timeCalc.xToTime( x )
    x = this.timeCalc.discreteX( x )
    t = this.timeCalc.xToTime( x )

    const y = this.timeCalc.yFromEvent( evt )

    if ( y > this.timeCalc.rootView.offsetHeight || this.timeCalc.inPopover( evt ) ) {
      if ( this.cursorIndicator ) {
        this.cursorIndicator.setState( { cursorX : null } )
      }
      if ( this.cursorTimeIndicator ) {
        this.cursorTimeIndicator.setState( { cursorTime : null } )
      }
      return
    }

    if ( this.cursorIndicator ) {
      this.cursorIndicator.setState( { cursorX : x } )
    }
    if ( this.cursorTimeIndicator ) {
      this.cursorTimeIndicator.setState( { cursorTime : t } )
    }
  }

  onMouseLeave = ( evt ) => {
    if ( this.cursorIndicator ) {
      this.cursorIndicator.setState( { cursorX : null } )
    }
    if ( this.cursorTimeIndicator ) {
      this.cursorTimeIndicator.setState( { cursorTime : null } )
    }
  }

  onCreateKeyframe = ( evt ) => {
    // evt: { value, keyframe, time, timeline }
    this.props.dispatch( ScenarioActions.setValue( evt.timeline.path, evt.value, evt.time, evt.timeline.attributeType ) )
  }

  onChangeKeyframeTime = ( evt ) => {
    // evt: { path, keyframe, timeline, time :<newTime>  }
    const { dispatch } = this.props
    const { kfMode, maxTime } = this.state
    const { timeline, keyframe, time } = evt
    const keyframes = timeline.keyframes
    if ( keyframes == null || time < 0 || time >= maxTime ) {
      return
    }

    const index = ArrayUtil.index( keyframes, 'id', keyframe.id )
    if ( index < 0 ) {
      console.log( 'cant find keyframe' )
      return
    } 

    const kf = keyframes[index]
    const dTime = time - kf.time
    const skf = keyframes[0]
    const ekf = keyframes[keyframes.length - 1]

    if ( kfMode == ScenarioScreen.KF_MODE_KEYFRAME ) {
      const indexAtT = ArrayUtil.index( keyframes, 'time', evt.time )
      if ( indexAtT == -1 ) {
        dispatch( ScenarioActions.changeTime( evt.path, evt.keyframe.id, evt.time ) )
      } // else there is a node at the time, stop the functioning
    } else if ( kfMode == ScenarioScreen.KF_MODE_LEFT ) {
      if ( skf.time + dTime < 0 ) {
        return
      }
      if ( index < keyframes.length - 1 ) {
        const rkf = keyframes[index + 1]
        if ( rkf.time <= time ) {
          return
        }      
      }  
      dispatch( ScenarioActions.shiftLeft( evt.path, evt.keyframe.id, evt.time ) )
    } else if ( kfMode == ScenarioScreen.KF_MODE_RIGHT ) {
      if ( ekf.time + dTime >= maxTime ) {
        return
      }
      if ( index > 0 ) {
        const lkf = keyframes[index - 1]
        if ( lkf.time >= time ) {
          return
        }
      }        
      dispatch( ScenarioActions.shiftRight( evt.path, evt.keyframe.id, evt.time ) )
    } else if ( kfMode == ScenarioScreen.KF_MODE_TIMELINE ) {
      if ( skf.time + dTime < 0 || ekf.time + dTime >= maxTime ) {
        return
      }        
      dispatch( ScenarioActions.shiftAll( evt.path, dTime ) )
    }
  }

  onChangeInterpolator = ( evt ) => {
    this.props.dispatch( ScenarioActions.setInterpolator( evt.interpolator, evt.timeline.path, evt.keyframe.id ) )
  }

  onRemoveKeyframe = ( evt ) => {
    // evt: { timeline, keyframe }
    this.props.dispatch( ScenarioActions.removeValue( evt.timeline.path, evt.keyframe.id ) )
  }

  onSetName = ( name, err ) => {
    if ( !err ) {
      this.props.dispatch( ScenarioActions.setName( name ) )
    }
  }

  onSetScale( scale ) {
    const sl = this.viewport.scrollLeft
    const cTime = this.timeCalc.xToTime( sl )
    scale = Math.floor( scale / 1000 ) * 1000
    if ( scale > 0 && scale != this.state.scale ) {
      this.setState( { scale } )
    }

    setTimeout( () => { 
      const x = this.timeCalc.timeToX( cTime )
      this.viewport.scrollLeft = x 
    }, 1 )
  }

  onSetCursorMode( mode ) {
    this.setState( { cursorMode : mode } )
  }

  onSetKeyframeMode( mode ) {
    this.setState( { kfMode : mode } )
  }

  onViewportScroll = ( e ) => {
    const sl = this.viewport.scrollLeft
    this.attributeView.scrollTop = this.viewport.scrollTop
    const t = this.timeCalc.xToTime( sl )
    if ( this.timeLabels ) {
      this.timeLabels.setState( { currentTime : t, scrollLeft : sl } )
    }
  }

  render() {
    const { scenarioData, dispatch, match : { params } } = this.props
    const { scenario, blueprint } = scenarioData

    if ( scenario == null || scenario.id !== params.scenarioId ) {
      return <Loading show message="Loading..." />
    }
    const { scale, maxTime, cursorMode, kfMode } = this.state

    this.timeCalc.mode = cursorMode
    this.timeCalc.scale = scale
    this.timeCalc.maxTime = maxTime

    const attrs = ( blueprint && blueprint.attributes ) || [];
    const tags = this.state.tags;

    this.treeOrganizer.tags = tags
    this.treeOrganizer.attributes = attrs
    const tree = this.treeOrganizer.getTree( )
    const editors = []

    const att = []
    const tls = []
    const vprops = { scale, maxTime, timeCalc : this.timeCalc }
    for ( let n = 0; n < tree.children.length; n++ ) {
      this.createEditors( tree.children[n], att, tls, vprops )
    }

    const suggestedTags = attrs ? getTagsForAttributes( attrs, true ) : []
    return (
      <div className={style.screen}>
        <div className={style.header}>
          <LabelGroup style={{ flex : 1 }}>
            <label style={{ minWidth : 0 }}>Name</label>
            <TextInput
              hint="Name..."
              value={scenario.name} 
              changeOnReturn 
              onChange={this.onSetName} />
            <Popup variant="primary" className={style.behavior} title="Edit Behavior">
              <BehaviorEditor scenarioData={scenarioData} dispatch={dispatch} />
            </Popup>
          </LabelGroup>
        </div>
        <div className={style.header}>
          <Tags
            tags={this.state.tags}
            autofocus={false}
            placeholder="Add filter"
            suggestions={suggestedTags || []}
            tagAdded={( tag, arr ) => { this.setState( { tags : arr } ); }}
            tagRemoved={( tag, arr ) => { this.setState( { tags : arr } ); }} />
          <div className={style.zoom}>
            <div className={style.label}>Zoom</div>
            <div className={style.zoomHolder}>
              <Slider
                min={0}
                max={ZOOM.length - 1}
                step={1}
                defaultValue={0} 
                onChange={( v ) => { this.onSetScale( ZOOM[v] * 1000 ) }} />
            </div>
          </div>
        </div>
        <div className={style.verticalScroll}>
          <div className={style.attributes} ref={( e ) => { this.attributeView = e; window.aaa = e }}>
            {att}
          </div>
          <div className={style.viewport} ref={e => this.viewport = e} onScroll={this.onViewportScroll}>
            <div
              className={style.timeline}
              onMouseMove={this.onMouseMove}
              onMouseLeave={this.onMouseLeave}
              style={{ width : this.timeCalc.widthAtScale() }}
              ref={( elem ) => { this.timeCalc.rootView = elem; window.bbb = elem }}>
              <TimeLabels ref={( ref ) => { this.timeLabels = ref }} timeCalc={this.timeCalc} />
              {tls}
              <CursorIndicator ref={( ref ) => { this.cursorIndicator = ref }} />
            </div>
          </div>
        </div>
        <div className={style.footer}>   
          <CursorTimeIndicator ref={( ref ) => { this.cursorTimeIndicator = ref }} />
          <LabelGroup>
            <label style={{ fontSize : '12px' }}>Cursor Precision</label>
            <ButtonGroup>
              <Button onClick={() => this.onSetCursorMode( TimeCalc.MODE_NONE )} bsStyle={cursorMode == TimeCalc.MODE_NONE ? 'primary' : 'default'} bsSize="xsmall" title="No clamping"><i className="fa fa-dot-circle-o fa-fw" /></Button>
              <Button onClick={() => this.onSetCursorMode( TimeCalc.MODE_1000 )} bsStyle={cursorMode == TimeCalc.MODE_1000 ? 'primary' : 'default'} bsSize="xsmall" title="Clamp to 1 second"><i className="fa fa-compress fa-fw" /></Button>
              <Button onClick={() => this.onSetCursorMode( TimeCalc.MODE_SCALE )} bsStyle={cursorMode == TimeCalc.MODE_SCALE ? 'primary' : 'default'} bsSize="xsmall" title="Clamp to scale"><i className="fa fa-expand fa-fw" /></Button>
            </ButtonGroup>
          </LabelGroup>
          <LabelGroup>
            <label style={{ fontSize : '12px' }}>Drag Behavior</label>
            <ButtonGroup>
              <Button onClick={() => this.onSetKeyframeMode( ScenarioScreen.KF_MODE_KEYFRAME )} bsStyle={kfMode == ScenarioScreen.KF_MODE_KEYFRAME ? 'primary' : 'default'} bsSize="xsmall" title="Keyframe"><i className="fa fa-circle-o fa-fw" /></Button>
              <Button onClick={() => this.onSetKeyframeMode( ScenarioScreen.KF_MODE_LEFT )} bsStyle={kfMode == ScenarioScreen.KF_MODE_LEFT ? 'primary' : 'default'} bsSize="xsmall" title="Slide left keyframes"><i className="fa fa-arrow-left fa-fw" /></Button>
              <Button onClick={() => this.onSetKeyframeMode( ScenarioScreen.KF_MODE_TIMELINE )} bsStyle={kfMode == ScenarioScreen.KF_MODE_TIMELINE ? 'primary' : 'default'} bsSize="xsmall" title="Slide timeline"><i className="fa fa-arrows-h fa-fw" /></Button>
              <Button onClick={() => this.onSetKeyframeMode( ScenarioScreen.KF_MODE_RIGHT )} bsStyle={kfMode == ScenarioScreen.KF_MODE_RIGHT ? 'primary' : 'default'} bsSize="xsmall" title="Slide right keyframes"><i className="fa fa-arrow-right fa-fw" /></Button>
            </ButtonGroup>
          </LabelGroup>
          <SavingIndicator id={scenario.id} />
        </div>
      </div>
    );
  }

  createEditors( node, attributeViews, timelineViews, vprops ) {

    const editors = [];
    if ( node.type == 'group' ) {
      this.createGroupEditor( node, attributeViews, timelineViews, vprops );
    } else {
      this.createAttributeEditor( node, attributeViews, timelineViews, vprops );
    }
  }

  createGroupEditor( node, attributeViews, timelineViews, vprops ) {
    console.log( node )
    if ( Util.isPathValid( node.pathname ) && Util.isPathCollection( node.pathname ) ) {
      return
    }
    const arr = node.children;
    const cavs = []
    const ctlvs = []
    for ( let n = 0; n < arr.length; n++ ) {
      if ( arr[n].type == 'group' ) {
        this.createGroupEditor( arr[n], cavs, ctlvs, vprops );
      } else {
        this.createAttributeEditor( arr[n], cavs, ctlvs, vprops ); 
      }
    }
    const name = StringUtil.camelCaseToTitleCase( node.name, true );
    attributeViews.push( <AttributeGroup key={`attg-${node.pathname}`} title={name} {...vprops}>{cavs}</AttributeGroup> )
    timelineViews.push( <TimelineGroup key={`tlg-${node.pathname}`} {...vprops}>{ctlvs}</TimelineGroup> )
  }

  createAttributeEditor( node, attributeViews, timelineViews, vprops ) {
    const scenario = this.props.scenarioData.scenario
    const attr = node.attribute
    if ( Util.isPathCollection( attr.path ) ) {
      return
    }
    const timeline = ArrayUtil.find( scenario.scenarioData.updaters, 'path', attr.path )
    const name = StringUtil.capitalizeAll( node.name )
    const aview = <AttributeView key={`att-${node.pathname}`} attribute={attr} title={name} {...vprops} />
    const tview = ( 
      <TimelineView
        key={`tl${node.pathname}`}
        attribute={attr} 
        timeline={timeline}
        onCreateKeyframe={this.onCreateKeyframe} 
        onChangeKeyframeTime={this.onChangeKeyframeTime}
        onChangeInterpolator={this.onChangeInterpolator}
        onRemoveKeyframe={this.onRemoveKeyframe}
        timeCalc={this.timeCalc}
        {...vprops} />
    )
    attributeViews.push( aview )
    timelineViews.push( tview )
  }
}

const ZOOM = [ 1, 2, 3, 4, 5, 8, 10, 15, 20, 30, 45, 60, 90, 120, 180, 300, 600, 900, 1800 ]

export default connect(
  state => ( {
    scenarioData : state.scenario
  } )
)( ScenarioScreen );
